import React, { useState, useEffect, useCallback } from "react";
import { Pagination, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AcademicYearSelectFilter from "../components/common/AcademicYearSelectFilter";
import TermSelectFilter from "../components/common/TermSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import NewsletterCard from "../components/newsletters/NewsletterCard";
import CreateNewsletterModal from "../components/newsletters/CreateNewsletterModal";
import ViewNewsletterModal from "../components/newsletters/ViewNewsletterModal";
import UpdateNewsletterModal from "../components/newsletters/UpdateNewsletterModal";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getNewsletters,
  getNewsletterById,
  deleteNewsletter,
  resetNewsletterServerStatus,
} from "../redux/actions/newsletterActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { getTerms } from "../redux/actions/termActions";
import { NewsletterResponseDto } from "../shared/dtos/newsletter/NewsletterResponseDto";
import { NewsletterAudience } from "../shared/enums/NewsletterAudience";
import { NEWSLETTER_AUDIENCE_OPTIONS } from "../shared/data/enumOptions";

const NewslettersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // Redux State
  const {
    items: newsletters,
    item: newsletter,
    pagination,
    loadingFetchAll: loadingNewsletters,
    loadingFetchOne: loadingNewsletter,
    errorFetchAll: errorLoadingNewsletters,
    errorFetchOne: errorLoadingNewsletter,
    serverStatus,
    loadingCreate: loadingNewsletterCreate,
    errorCreate: errorCreatingNewsletter,
    loadingUpdate: loadingNewsletterUpdate,
    errorUpdate: errorUpdatingNewsletter,
    loadingDelete: loadingNewsletterDelete,
  } = useAppSelector((state) => state.newsletter);

  // Lookups
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { items: terms } = useAppSelector((state) => state.term);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedYearId, setSelectedYearId] = useState<string>("");
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [selectedAudience, setSelectedAudience] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 12 });

  // Modal State
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] =
    useState<NewsletterResponseDto | null>(null);

  // Permissions
  const canCreate = hasPermission("newsletter:create");
  const canUpdate = hasPermission("newsletter:update");
  const canDelete = hasPermission("newsletter:delete");
  const canRead = hasPermission("newsletter:read_all");

  // Load Lookups
  useEffect(() => {
    if (!academicYears.length) dispatch(getAcademicYears());
    if (!terms.length) dispatch(getTerms());
  }, [dispatch, academicYears.length, terms.length]);

  const fetchList = useCallback((): void => {
    dispatch(
      getNewsletters({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        academicYearId: selectedYearId || undefined,
        termId: selectedTermId || undefined,
        audienceScope: (selectedAudience as NewsletterAudience) || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig,
    debouncedSearch,
    selectedBranchId,
    selectedYearId,
    selectedTermId,
    selectedAudience,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingNewsletterCreate) setIsCreateOpen(false);
  }, [loadingNewsletterCreate]);

  const handleOpenUpdate = (item: NewsletterResponseDto): void => {
    dispatch(getNewsletterById(item.id));
    setSelectedItem(item);
    setIsUpdateOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingNewsletterUpdate) {
      setIsUpdateOpen(false);
      setSelectedItem(null);
    }
  }, [loadingNewsletterUpdate]);

  const handleOpenView = (item: NewsletterResponseDto): void => {
    dispatch(getNewsletterById(item.id));
    setIsViewOpen(true);
  };
  const handleCloseView = (): void => setIsViewOpen(false);

  const handleOpenDelete = (item: NewsletterResponseDto): void => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingNewsletterDelete) {
      setIsDeleteOpen(false);
      setSelectedItem(null);
    }
  }, [loadingNewsletterDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedItem) {
      dispatch(deleteNewsletter(selectedItem.id));
    }
  };

  // Auto-close on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetNewsletterServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="School Newsletters"
      subtitle="Draft and manage terminal circulars and general school communications."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            New Newsletter
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search by title..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />

        <AcademicYearSelectFilter
          value={selectedYearId}
          onChange={setSelectedYearId}
        />
        <TermSelectFilter value={selectedTermId} onChange={setSelectedTermId} />
        <AppSelectFilter
          label="Audience Scope"
          value={selectedAudience}
          onChange={setSelectedAudience}
          options={NEWSLETTER_AUDIENCE_OPTIONS}
          allLabel="All Audiences"
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingNewsletters}
        error={errorLoadingNewsletters}
        itemCount={newsletters?.length || 0}
        emptyMessage="No newsletters published yet"
        onRetry={fetchList}
      >
        {newsletters.map((item) => (
          <NewsletterCard
            key={item.id}
            newsletter={item}
            onView={canRead ? () => handleOpenView(item) : undefined}
            onEdit={canUpdate ? () => handleOpenUpdate(item) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(item) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* Pagination Controls */}
      {pagination?.totalPages && pagination.totalPages > 1 && (
        <Stack sx={{ alignItems: "center", mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={pageConfig.page}
            onChange={(_, val) =>
              setPageConfig((prev) => ({ ...prev, page: val }))
            }
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* CREATE MODAL */}
      {isCreateOpen && (
        <CreateNewsletterModal
          open={isCreateOpen}
          onClose={handleCloseCreate}
          loading={loadingNewsletterCreate}
          error={errorCreatingNewsletter}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateOpen && selectedItem && (
        <UpdateNewsletterModal
          open={isUpdateOpen}
          onClose={handleCloseUpdate}
          newsletter={
            newsletter?.id === selectedItem.id ? newsletter : selectedItem
          }
          loading={loadingNewsletterUpdate || loadingNewsletter}
          error={errorUpdatingNewsletter}
        />
      )}

      {/* VIEW MODAL */}
      {isViewOpen && (
        <ViewNewsletterModal
          open={isViewOpen}
          onClose={handleCloseView}
          newsletter={newsletter}
          loading={loadingNewsletter}
          error={errorLoadingNewsletter}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteOpen && selectedItem && (
        <ConfirmDialog
          open={isDeleteOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Newsletter"
          description={`Are you sure you want to delete "${selectedItem?.title}"?`}
          loading={loadingNewsletterDelete}
        />
      )}
    </PageContainer>
  );
};

export default NewslettersPage;
